import { FC, Fragment } from 'react';
import CellValue from '@app/shared/components/cell-value/CellValue';
import { decimalCalculatedViewRules } from '@app/shared/components/cell-value/CellValueConfigurations';
import { useAppSelector } from '@app/core/hooks/redux-hooks';
import { enumKeyByValue } from '@app/shared/helpers';
import {
  WaterfallSections,
  WaterfallSectionsErf,
} from '@app/shared/models/contracts/enums/shared-enums';
import { Alignment } from '@app/shared/enums/alignment.enum';
import TableSpacerRow from '@app/shared/components/table/TableSpacerRow';
import classNames from 'classnames';
import { WaterfallTableExpandableRow } from '@app/modules/projects/outputs/pwerm2-summary-waterfall/waterfall-table/WaterfallTableExpandableRow';
import { WaterfallPartialTableProps } from '../../pwerm2-summary-waterfall/waterfall-table/waterfall-shared';
import { getWaterfallInstrumentValue } from '@app/core/store/pwerm2-calculation-slice-selectors';
import { useLocale } from '@app/core/hooks/useLocale';
import { selectValuedInstruments } from '@app/core/store/capital-structure-slice-selectors';
import useTogglerPwermWaterfall from '@app/core/hooks/useTogglerPwermWaterfall';
import useInstrumentSort from '@app/shared/hooks/useInstrumentSort';
import { ValuationInstrumentTotal } from './ValuationInstrumentTotal';
import { InstrumentValuation } from './InstrumentValuation';
import { InstrumentInterimCashflows } from './InstrumentInterimCashflows';
import { getInstrumentTransactions } from './utils/transactionUtils';

export const Pwerm2WaterfallValuationPartialTable: FC<WaterfallPartialTableProps> = ({
  columnsCount,
}): JSX.Element => {
  const valuedInstruments = useAppSelector(selectValuedInstruments);
  const sortedValuedInstruments = useInstrumentSort(valuedInstruments);
  const results = useAppSelector((state) => state.pwerm2Calculation.calculatedResults);
  const maxDataColspanCount = columnsCount - 1;
  const { l } = useLocale();
  const interpolationOption = {
    escapeValue: false,
  };

  const pwermWaterfallTableStates = useAppSelector(
    (state) => state.uiValues.pwermWaterfallTableExpandStates
  );
  const { handleToggle } = useTogglerPwermWaterfall(WaterfallSectionsErf.Valuation);

  return (
    <>
      <tbody data-testid="valuation-expandable-row">
        <WaterfallTableExpandableRow
          title={l('_Valuation')}
          id={enumKeyByValue(WaterfallSections, WaterfallSections.Valuation)}
          isOpen={pwermWaterfallTableStates[WaterfallSectionsErf.Valuation]}
          setIsOpen={handleToggle}
          colCount={columnsCount}
        />
      </tbody>
      <tbody
        className={classNames('table-primary__table-section', {
          'table-primary__table-section--collapsed':
            !pwermWaterfallTableStates[WaterfallSectionsErf.Valuation],
        })}
        data-testid="valuation-section">
        {sortedValuedInstruments.map((instrument) => {
          const instrumentTransactions = getInstrumentTransactions(
            results,
            instrument.instrumentId
          );
          const hasInterims = instrumentTransactions.hasInterims;
          const hasMultipleTranches = Boolean(
            instrument.payoutLogic && instrument.payoutLogic.length > 1
          );

          return (
            <Fragment key={`valuation-item-instrument-${instrument.instrumentId}`}>
              <tr data-testid={`instrument-header-row-${instrument.instrumentId}`}>
                <th
                  className="table-primary__cell--header-quaternary"
                  data-testid={`instrument-header-${instrument.instrumentId}`}>
                  {instrument.instrumentNarrative}
                </th>
                <th
                  className="table-primary__cell--header-quaternary"
                  colSpan={maxDataColspanCount}
                />
              </tr>

              {hasInterims ? (
                <tr>
                  <th className="table-primary__cell--header-tertiary">{l('_InterimCashFlows')}</th>
                  <th
                    className="table-primary__cell--header-tertiary"
                    colSpan={maxDataColspanCount}
                  />
                </tr>
              ) : null}

              <InstrumentInterimCashflows
                instrument={instrument}
                columnsCount={columnsCount}
                instrumentTransactions={instrumentTransactions}
              />

              {(!instrument.payoutLogic?.length || instrument.payoutLogic.length <= 1) && (
                <InstrumentValuation
                  instrument={instrument}
                  tranche={instrument.payoutLogic?.[0]}
                  trancheIndex={instrument.payoutLogic?.[0] ? 1 : undefined}
                  columnsCount={columnsCount}
                  hasInterims={hasInterims}
                  interpolationOption={interpolationOption}
                  hasMultipleTranches={hasMultipleTranches}
                />
              )}

              {hasMultipleTranches && (
                <>
                  {instrument.payoutLogic!.map((tranche, index) => (
                    <Fragment key={`valuation-item-tranche-${tranche.id}`}>
                      {index !== 0 && <TableSpacerRow />}
                      {!hasInterims && (
                        <tr data-testid={`tranche-header-row-${tranche.id}`}>
                          <th
                            className="table-primary__cell--header-tertiary"
                            data-testid={`tranche-header-${tranche.id}`}>
                            {l('_TrancheN', { n: index + 1 })}
                          </th>
                          <th
                            className="table-primary__cell--header-tertiary"
                            colSpan={columnsCount - 1}
                          />
                        </tr>
                      )}
                      <InstrumentValuation
                        instrument={instrument}
                        tranche={tranche}
                        trancheIndex={index + 1}
                        columnsCount={columnsCount}
                        hasInterims={hasInterims}
                        interpolationOption={interpolationOption}
                        hasMultipleTranches={hasMultipleTranches}
                      />
                    </Fragment>
                  ))}
                </>
              )}
              {(hasMultipleTranches || hasInterims) && (
                <ValuationInstrumentTotal
                  instrument={instrument}
                  columnsCount={columnsCount}
                  interpolationOption={interpolationOption}
                  hasInterims={hasInterims}
                  hasMultipleTranches={hasMultipleTranches}
                />
              )}
              <tr data-testid={`value-per-share-row-${instrument.instrumentId}`}>
                <th className="table-primary__cell--vertical-separator table-primary__cell--background-tertiary table-primary__cell--strong">
                  {l('_ValuePerShare')}
                </th>
                <td className="table-primary__cell--vertical-separator table-primary__cell--background-tertiary" />
                <td
                  colSpan={columnsCount - 2}
                  className="table-primary__cell--background-tertiary table-primary__cell--strong">
                  <CellValue
                    {...decimalCalculatedViewRules}
                    alignment={Alignment.Center}
                    value={getWaterfallInstrumentValue(
                      results,
                      instrument.instrumentId,
                      (instrument) => instrument.weightedDiscountedFairValuePerShare
                    )}
                    dataTestId={`value-per-share-cell-${instrument.instrumentId}`}
                  />
                </td>
              </tr>
              <tr>
                <td colSpan={columnsCount} className="table-primary__cell--section-separator" />
              </tr>
            </Fragment>
          );
        })}
      </tbody>
    </>
  );
};
